import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link } from 'react-router-dom';
import { getDictionaryPreview, KC_DICTIONARY_BASE } from '../repository';
import { KC_COLORS } from '../theme';

function DictionaryTermLink({ href, className, style, children }) {
  if (href?.startsWith('/knowledge-center/')) {
    return (
      <Link to={href} className={className} style={style}>
        {children}
      </Link>
    );
  }
  if (!href) {
    return (
      <span className={className} style={{ ...style, opacity: 0.72 }}>
        {children}
      </span>
    );
  }
  return (
    <a href={href} className={className} style={style}>
      {children}
    </a>
  );
}

export function DictionaryPreview() {
  const preview = getDictionaryPreview();
  if (!preview) return null;

  const previewLetters = preview.previewLetters || ['A', 'B', 'C'];
  const groups = preview.groups || [];

  return (
    <div
      id="kc-dictionary"
      className="kc-dictionary h-full min-h-0 scroll-mt-28 flex flex-col"
      style={{ textAlign: 'left' }}
    >
      <div className="flex items-center justify-between gap-3 mb-5 shrink-0">
        <h2
          className="kc-section-title kc-type-h3 uppercase tracking-[0.08em] m-0"
          style={{ color: KC_COLORS.primaryNavy }}
        >
          Insurance AI Dictionary
        </h2>
        <Link
          to={KC_DICTIONARY_BASE}
          className="kc-type-body font-semibold no-underline whitespace-nowrap"
          style={{ color: KC_COLORS.teal }}
        >
          View all glossary →
        </Link>
      </div>

      <div
        className="flex-1 min-h-0 flex flex-col rounded-2xl border bg-white p-4 sm:p-5 overflow-hidden"
        style={{
          borderColor: KC_COLORS.border,
          boxShadow: '0 10px 28px rgba(10,29,58,0.06)',
        }}
      >
        <div className="shrink-0 flex flex-wrap gap-1 pb-3 border-b" style={{ borderColor: KC_COLORS.border }}>
          {(preview.letters || []).map((letter) => {
            const isPreviewLetter = previewLetters.includes(letter);
            const hasTerms = groups.some(
              (group) => group.letter === letter && group.terms.length > 0
            );

            return (
              <span
                key={letter}
                className="inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded kc-type-caption font-semibold"
                style={{
                  backgroundColor: isPreviewLetter && hasTerms ? KC_COLORS.teal : 'transparent',
                  color: isPreviewLetter
                    ? hasTerms
                      ? KC_COLORS.white
                      : KC_COLORS.primaryNavy
                    : '#D1D5DB',
                }}
                aria-label={`Dictionary letter ${letter}${isPreviewLetter ? ' (preview)' : ''}`}
              >
                {letter}
              </span>
            );
          })}
        </div>

        <div className="flex-1 min-h-0 mt-4 grid grid-cols-3 gap-3 sm:gap-5 overflow-hidden">
          {groups.map((group) => (
            <div key={group.letter} className="min-h-0 flex flex-col overflow-hidden">
              <div
                className="mb-2.5 shrink-0 font-bold"
                style={{
                  color: KC_COLORS.teal,
                  fontSize: '22px',
                  lineHeight: '28px',
                  letterSpacing: '0.02em',
                }}
              >
                {group.letter}
              </div>
              <ul className="m-0 p-0 list-none flex flex-col gap-2 overflow-y-auto min-h-0 pr-1">
                {(group.terms || []).length ? (
                  group.terms.map((item) => (
                    <li key={item.term}>
                      <DictionaryTermLink
                        href={item.href}
                        className="kc-type-body no-underline transition-colors hover:underline block"
                        style={{
                          color: KC_COLORS.primaryNavy,
                          fontSize: '13px',
                          lineHeight: '18px',
                          fontWeight: 500,
                        }}
                      >
                        {item.term}
                      </DictionaryTermLink>
                    </li>
                  ))
                ) : (
                  <li
                    className="kc-type-caption"
                    style={{ color: KC_COLORS.gray600, lineHeight: '18px' }}
                  >
                    —
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="shrink-0 mt-4 pt-3 border-t flex items-center justify-between gap-3"
          style={{ borderColor: KC_COLORS.border }}
        >
          <p className="m-0 kc-type-caption" style={{ color: KC_COLORS.gray600 }}>
            Showing A–C preview
          </p>
          <Link
            to={KC_DICTIONARY_BASE}
            className="inline-flex items-center gap-1 font-semibold no-underline transition-all hover:gap-2"
            style={{ color: KC_COLORS.teal, fontSize: '13px', lineHeight: '18px' }}
          >
            Full A–Z glossary
            <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
          </Link>
        </div>
      </div>
    </div>
  );
}
